import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAppSelector } from '@/app/hooks';
import { OrderItem } from '@/types/supervisor';
import type { OrderItem as StoreOrderItem } from '@/features/order/orderSlice';
import { getPaperTypes, type PaperType } from '@/services/productService';
import {
  selectOrderItems,
} from '@/features/order/orderSlice';
import { ScrollArea } from '@/components/ui/scroll-area';
import useDebouncedPricingPreview from '@/components/order/hooks/useDebouncedPricingPreview';
import { analytics } from '@/services/analytics';
import { logging } from '@/services/logging';

interface SetPaperTypeWindowProps {
  readonly item?: OrderItem | null;
  readonly onClose: () => void;
  readonly onBack?: () => void;
  readonly onSave: (paperTypeId: string) => void | boolean | Promise<void | boolean>;
  readonly saveLabel?: string;
  readonly selectedPaperTypeId?: string | null;
  readonly onSelectPaperType?: (paperTypeId: string) => void;
  readonly onConfirmSelection?: (paperTypeId: string) => void;
  readonly onApplyPaperToAll?: (paperTypeId: string) => Promise<void> | void;
  readonly draftPaperTypeId?: string | null;
  readonly onDraftChange?: (paperTypeId: string | null) => void;
  readonly onDraftReset?: () => void;
}

const filterActivePapers = (papers: readonly PaperType[]): PaperType[] => {
  return papers.filter(paper => {
    if (typeof (paper as PaperType & { available?: boolean }).available === 'boolean') {
      return (paper as PaperType & { available?: boolean }).available;
    }
    return true;
  });
};

const useLiveOrderItem = (identifier: string | null | undefined): OrderItem | null => {
  const items = useAppSelector(selectOrderItems);
  const live = useMemo(() => {
    if (!identifier) {
      return null;
    }

    const candidate = items.find(current => current.id === identifier);
    if (!candidate) {
      return null;
    }

    const resolveStatus = (item: StoreOrderItem): OrderItem['status'] => {
      const upper = typeof item.status === 'string' ? item.status.trim().toUpperCase() : undefined;
      if (upper === 'IN_ORDER' || upper === 'READY_TO_PRINT' || upper === 'PRINTED') {
        return upper;
      }
      return 'NEW';
    };

    return {
      id: candidate.id,
      uploadedFileId: candidate.uploadedFileId ?? undefined,
      paperTypeId: candidate.paperTypeId ?? undefined,
      printerId: candidate.printerId ?? undefined,
      orderId: candidate.orderId ?? null,
      width: candidate.width ?? undefined,
      height: candidate.height ?? undefined,
      widthIn: candidate.widthIn ?? undefined,
      heightIn: candidate.heightIn ?? undefined,
      originalWidthPx: candidate.originalWidthPx ?? undefined,
      originalHeightPx: candidate.originalHeightPx ?? undefined,
      status: resolveStatus(candidate),
      createdAt: candidate.createdAt ? String(candidate.createdAt) : undefined,
      updatedAt: candidate.updatedAt ? String(candidate.updatedAt) : undefined,
      price: candidate.price ?? undefined,
      pricingStatus: (candidate.pricingStatus as OrderItem['pricingStatus']) ?? undefined,
      pricingError: candidate.pricingError ?? undefined,
    } satisfies OrderItem;
  }, [identifier, items]);
  return live ?? null;
};

const SetPaperTypeWindow: React.FC<SetPaperTypeWindowProps> = ({
  item: incomingItem,
  onClose,
  onBack,
  onSave,
  saveLabel,
  selectedPaperTypeId: controlledSelectedPaperTypeId,
  onSelectPaperType,
  onConfirmSelection,
  onApplyPaperToAll,
  draftPaperTypeId,
  onDraftChange,
  onDraftReset,
}) => {
  const { t } = useTranslation();
  const targetIdentifier = incomingItem?.id ?? null;
  const liveItem = useLiveOrderItem(targetIdentifier);
  const item = liveItem ?? incomingItem ?? null;

  const [paperTypes, setPaperTypes] = useState<PaperType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [internalSelectedPaperTypeId, setInternalSelectedPaperTypeId] = useState<string | null>(item?.paperTypeId ?? null);
  const [isSaving, setIsSaving] = useState(false);
  const [isApplyingAll, setIsApplyingAll] = useState(false);

  const { requestPreview } = useDebouncedPricingPreview();

  useEffect(() => {
    let cancelled = false;
    const fetchPapers = async () => {
      try {
        setIsLoading(true);
        const fetched = await getPaperTypes();
        if (cancelled) {
          return;
        }
        const active = filterActivePapers(fetched);
        setPaperTypes(active);
        setLoadError(null);
      } catch (error) {
        logging.error('Failed to load paper types', { error });
        if (!cancelled) {
          setLoadError(t('setPaperType.loadError', 'Unable to load paper options.'));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchPapers();

    return () => {
      cancelled = true;
    };
  }, [t]);

  const draftChangeHandler: ((paperTypeId: string | null) => void) | null =
    typeof onDraftChange === 'function' ? onDraftChange : null;
  const isControlled = typeof controlledSelectedPaperTypeId !== 'undefined';
  const isDraftControlled = draftChangeHandler !== null;
  const draftSelection = typeof draftPaperTypeId === 'undefined' ? null : draftPaperTypeId;
  const effectiveSelectedPaperTypeId = isDraftControlled
    ? (draftSelection ?? item?.paperTypeId ?? null)
    : (isControlled ? controlledSelectedPaperTypeId ?? null : internalSelectedPaperTypeId);

  const emitDraftChange = useCallback((next: string | null) => {
    if (draftChangeHandler) {
      draftChangeHandler(next);
    }
  }, [draftChangeHandler]);

  useEffect(() => {
    if (isDraftControlled) {
      return;
    }
    if (!isControlled) {
      setInternalSelectedPaperTypeId(item?.paperTypeId ?? null);
    }
  }, [isControlled, isDraftControlled, item?.id, item?.paperTypeId]);

  const handleClose = useCallback(() => {
    analytics.track('button: ui.paper_close', { itemId: item?.id ?? null });
    onClose();
  }, [item?.id, onClose]);

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack();
      return;
    }
    handleClose();
  }, [handleClose, onBack]);

  const handlePaperSelect = useCallback((paperTypeId: string) => {
    if (!item) {
      return;
    }

    analytics.track('button: ui.paper_select', { itemId: item.id, paperTypeId });

    if (isDraftControlled) {
      emitDraftChange(paperTypeId);
      return;
    }

    if (isControlled) {
      onSelectPaperType?.(paperTypeId);
    } else {
      setInternalSelectedPaperTypeId(paperTypeId);
    }
  }, [emitDraftChange, isControlled, isDraftControlled, item, onSelectPaperType]);

  const filteredPaperTypes = paperTypes;

  const selectedPaper = useMemo(() => {
    if (!effectiveSelectedPaperTypeId) {
      return null;
    }
    return paperTypes.find(paper => paper.id === effectiveSelectedPaperTypeId) ?? null;
  }, [effectiveSelectedPaperTypeId, paperTypes]);

  const handleSave = useCallback(async () => {
    if (!item || !effectiveSelectedPaperTypeId) {
      return;
    }

    try {
      setIsSaving(true);
      const shouldClose = await Promise.resolve(onSave(effectiveSelectedPaperTypeId));
      analytics.track('button: ui.paper_save', { itemId: item.id, paperTypeId: effectiveSelectedPaperTypeId });

      if (isDraftControlled) {
        emitDraftChange(null);
        return;
      }

      if (shouldClose === false) {
        return;
      }

      onDraftReset?.();
      emitDraftChange(null);
      handleClose();
    } finally {
      setIsSaving(false);
    }
  }, [
    emitDraftChange,
    effectiveSelectedPaperTypeId,
    handleClose,
    isDraftControlled,
    item,
    onSave,
    onDraftReset,
  ]);

  const handleApplyPaperToAll = useCallback(async () => {
    if (!effectiveSelectedPaperTypeId || !onApplyPaperToAll) {
      return;
    }
    try {
      setIsApplyingAll(true);
      analytics.track('button: ui.paper_apply_all', { paperTypeId: effectiveSelectedPaperTypeId });
      await Promise.resolve(onApplyPaperToAll(effectiveSelectedPaperTypeId));
    } finally {
      setIsApplyingAll(false);
    }
  }, [effectiveSelectedPaperTypeId, onApplyPaperToAll]);

  if (!item) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-lg font-semibold text-muted-foreground">
          {t('setPaperType.noItem', 'Select an item to choose its paper type.')}
        </p>
        <Button variant="outline" onClick={handleBack}>
          {t('common.back', 'Back')}
        </Button>
      </div>
    );
  }

  const headerTitle = t('setPaperType.title', 'Choose paper type');
  const headerSubtitle = t('setPaperType.subtitle', 'Pick the ideal medium for this print.');
  const saveButtonLabel = saveLabel ?? t('setPaperType.confirmCta', 'Confirm paper type');

  useEffect(() => {
    if (!item?.uploadedFileId) {
      requestPreview({ type: 'reset' });
      return;
    }

    if (!effectiveSelectedPaperTypeId) {
      requestPreview({ type: 'reset' });
      return;
    }

    requestPreview({
      type: 'preview',
      payload: {
        uploadedFileId: item.uploadedFileId,
        paperTypeId: effectiveSelectedPaperTypeId,
        printerId: item.printerId,
        widthInches: item.widthIn,
        heightInches: item.heightIn,
        galleryWrapInches: item.galleryWrapIn,
      },
    });
  }, [effectiveSelectedPaperTypeId, item?.galleryWrapIn, item?.heightIn, item?.id, item?.printerId, item?.uploadedFileId, item?.widthIn, requestPreview]);

  return (
    <div className="mx-auto flex h-full w-full max-w-5xl flex-col overflow-hidden bg-background p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={handleBack} aria-label={t('common.back', 'Back')}>
            <ArrowLeft className="h-5 w-5" aria-hidden />
          </Button>
          <div>
            <p className="text-lg font-semibold text-foreground sm:text-xl">{headerTitle}</p>
            <p className="text-sm text-muted-foreground">{headerSubtitle}</p>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2">
          <Button variant="outline" onClick={handleClose}>
            {t('common.cancel', 'Cancel')}
          </Button>
          {onApplyPaperToAll ? (
            <Button
              variant="secondary"
              onClick={handleApplyPaperToAll}
              disabled={isSaving || isApplyingAll || !effectiveSelectedPaperTypeId}
            >
              {isApplyingAll ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t('setPaperType.applyAllCta', 'Apply to all items')}
            </Button>
          ) : null}
          <Button onClick={handleSave} disabled={isSaving || !effectiveSelectedPaperTypeId}>
            {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {saveButtonLabel}
          </Button>
        </div>
      </div>

      <div className="flex-1 min-h-0 py-4">
        <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:gap-6">
          <section className="flex flex-col gap-4 rounded-2xl border border-border bg-muted/10 p-4 sm:p-6">
            <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-muted-foreground">
                  {t('setPaperType.selectionLabel', 'Available paper types')}
                </span>
                <span className="text-xs text-muted-foreground sm:text-sm">
                  {t('setPaperType.selectionHelper', 'Select a paper to see its details.')}
                </span>
              </div>
            </header>

            <div className="max-h-[60vh] overflow-y-auto pr-2">
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {isLoading ? (
                  <div className="col-span-full flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>{t('common.loading', 'Loading...')}</span>
                  </div>
                ) : null}

                {loadError ? (
                  <div className="col-span-full rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
                    {loadError}
                  </div>
                ) : null}

                {!isLoading && !loadError && filteredPaperTypes.length === 0 ? (
                  <div className="col-span-full rounded-lg border border-border bg-background/70 p-6 text-center text-sm text-muted-foreground">
                    {t('setPaperType.emptyState', 'No papers match your search.')}
                  </div>
                ) : null}

                {filteredPaperTypes.map(paper => {
                  const isSelected = effectiveSelectedPaperTypeId === paper.id;
                  const hasDimensions = typeof paper.widthIn === 'number' && typeof paper.lengthIn === 'number';
                  const priceIndexValue = typeof paper.priceIndex === 'number' && Number.isFinite(paper.priceIndex)
                    ? paper.priceIndex
                    : null;
                  const priceIndexLabel = priceIndexValue != null ? priceIndexValue.toFixed(2) : '—';
                  return (
                    <button
                      key={paper.id}
                      type="button"
                      onClick={() => handlePaperSelect(paper.id)}
                      onDoubleClick={() => {
                        analytics.track('button: ui.paper_confirm_double', { itemId: item?.id ?? null, paperTypeId: paper.id });
                        handlePaperSelect(paper.id);
                        onConfirmSelection?.(paper.id);
                      }}
                      className={`flex flex-col rounded-xl border p-4 text-left transition focus-visible:outline-none focus-visible:ring focus-visible:ring-primary/50 ${
                        isSelected
                          ? 'border-primary bg-primary/10 shadow'
                          : 'border-border bg-card hover:border-primary/40'
                      }`}
                    >
                      <span className="text-sm font-semibold text-foreground">
                        {paper.name}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {t('setPaperType.producerLabel', 'Producer: {{brand}}', { brand: paper.brand })}
                      </span>
                      <span className="mt-1 text-xs text-muted-foreground">
                        {t('setPaperType.priceIndexInline', 'Price index: {{index}}', { index: priceIndexLabel })}
                      </span>
                      {paper.category ? (
                        <span className="mt-2 inline-flex w-fit items-center rounded-full bg-primary/10 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary">
                          {t(`paperCategory.${paper.category.toLowerCase()}`, paper.category)}
                        </span>
                      ) : null}
                      {'weightGsm' in paper ? (
                        <span className="mt-2 text-xs text-muted-foreground">
                          {t('setPaperType.weightLabel', 'Weight: {{value}} gsm', {
                            value: (paper as PaperType & { weightGsm?: number }).weightGsm,
                          })}
                        </span>
                      ) : null}
                      {hasDimensions ? (
                        <span className="mt-2 text-xs text-muted-foreground">
                          {t('setPaperType.maxDimensionsLabel', 'Max print dimensions: {{width}}" × {{height}}"', {
                            width: paper.widthIn,
                            height: paper.lengthIn,
                          })}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          <aside className="flex min-h-[320px] flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
            {selectedPaper ? (
              'description' in selectedPaper && (selectedPaper as PaperType & { description?: string }).description ? (
                <ScrollArea className="h-full flex-1 p-4 sm:p-6">
                  <div
                    className="prose prose-sm dark:prose-invert"
                    dangerouslySetInnerHTML={{
                      __html: (selectedPaper as PaperType & { description?: string }).description ?? '',
                    }}
                  />
                </ScrollArea>
              ) : (
                <div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
                  {t('setPaperType.noDescription', 'This paper does not have a description yet.')}
                </div>
              )
            ) : (
              <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-muted-foreground">
                <p>{t('setPaperType.noPaperSelected', 'Please select a paper to see its detailed description.')}</p>
              </div>
            )}
          </aside>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-2 sm:hidden">
        <Button variant="outline" onClick={handleClose}>
          {t('common.cancel', 'Cancel')}
        </Button>
        {onApplyPaperToAll ? (
          <Button
            variant="secondary"
            onClick={handleApplyPaperToAll}
            disabled={isSaving || isApplyingAll || !effectiveSelectedPaperTypeId}
          >
            {isApplyingAll ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t('setPaperType.applyAllCta', 'Apply to all items')}
          </Button>
        ) : null}
        <Button onClick={handleSave} disabled={isSaving || !effectiveSelectedPaperTypeId}>
          {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {saveButtonLabel}
        </Button>
      </div>
    </div>
  );
};

export default SetPaperTypeWindow;
